import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Target, Scales, TrendUp, Brain, ChartLine, Warning, Lightning, ArrowRight, CheckCircle, XCircle, BookOpen, Crosshair } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const ComoMelhorarRiskReward = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Como Melhorar Risk Reward no Trading | Guia Completo',
      description: 'Aprenda como melhorar seu risk reward no trading e aumentar sua consistência com melhores decisões e execução.',
      path: '/pt/como-melhorar-risk-reward',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/como-melhorar-risk-reward'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Risk Reward</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-[#00FF85] text-sm font-medium mb-4">
          <Scales size={16} weight="bold" />
          <span>Gestão de Risco</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
          Como Melhorar Seu Risk Reward no Trading
        </h1>

        <p className="text-lg text-gray-400 mb-12">
          Guia prático para aumentar sua lucratividade com melhores decisões de risco/retorno
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Muitos traders se obsessão com <strong className="text-white">win rate</strong> — querem acertar o máximo de trades possível. Mas ignoram uma métrica ainda mais importante: o <strong className="text-white">risk reward</strong>.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            A verdade é que você pode ter um win rate de 40% e ser extremamente lucrativo. Ou ter um win rate de 70% e perder dinheiro. Tudo depende de quanto você ganha quando acerta versus quanto perde quando erra. Esse é o poder do risk reward.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Scales size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que é Risk Reward</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Risk reward (RR) é a relação entre quanto você arrisca e quanto você busca ganhar em um trade. É uma das métricas mais fundamentais do trading.
            </p>

            <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-[#00FF85] mb-4">Exemplo Simples:</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Você arrisca</p>
                  <p className="text-2xl font-bold text-red-400">R$ 100</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Você busca ganhar</p>
                  <p className="text-2xl font-bold text-green-400">R$ 200</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Risk Reward</p>
                  <p className="text-2xl font-bold text-[#00FF85]">1:2</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Com um RR de 1:2, você precisa acertar apenas 33% dos trades para empatar. Acima disso, você lucra. Isso muda completamente a matemática do trading.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Traders Têm um Risk Reward Ruim</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A maioria dos traders tem um RR negativo — perdem mais quando erram do que ganham quando acertam. Por quê?
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Cortar Lucro Cedo Demais</h3>
                </div>
                <p className="text-gray-400 text-sm">O trade está positivo, você fica ansioso, fecha com +R$ 50 quando poderia ter ido a +R$ 200. Medo de perder o que já ganhou.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Deixar Prejuízo Correr</h3>
                </div>
                <p className="text-gray-400 text-sm">O trade está negativo, você espera "voltar", move o stop, ou simplesmente ignora. Uma perda de R$ 100 vira R$ 300.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Decisões Emocionais</h3>
                </div>
                <p className="text-gray-400 text-sm">No momento do trade, a emoção assume. Você abandona o plano, reage ao mercado, e destrói seu RR planejado.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O resultado? Um padrão devastador: <em className="text-white">ganhos pequenos + perdas grandes = conta negativa</em>, mesmo com win rate alto.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Psicologia do Risk Reward</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O problema do RR não é técnico — é psicológico. Nosso cérebro não foi projetado para lidar bem com risco e recompensa financeira.
            </p>

            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <BookOpen size={28} className="text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">"Trading in the Zone" — Mark Douglas</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Douglas explica que o <strong className="text-white">medo de perder lucro</strong> é tão poderoso quanto o medo de perder dinheiro. Quando um trade está positivo, a mente grita para proteger o ganho — mesmo que isso destrua o RR planejado.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Medo de Perder Lucro</h3>
                <p className="text-gray-400 text-sm">Você tem +R$ 80, o alvo é +R$ 200. Mas a voz na sua cabeça diz: "E se voltar? Fecha agora!" E você fecha.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Dificuldade de Segurar Trades</h3>
                <p className="text-gray-400 text-sm">Cada movimento contra você parece o fim do mundo. Cada pausa no preço gera ansiedade. Segurar até o alvo exige disciplina brutal.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Entender essa psicologia é o primeiro passo. O segundo é criar sistemas que protejam você de si mesmo.
            </p>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Como Melhorar Risk Reward na Prática</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Melhorar o RR exige mudanças concretas na forma como você planeja e executa trades.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Defina o Alvo ANTES de Entrar</h3>
                    <p className="text-gray-400 text-sm">Não entre sem saber onde vai sair. O alvo deve ser definido antes do trade, baseado em análise técnica, não em "quanto você quer ganhar".</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Stop Baseado em Lógica</h3>
                    <p className="text-gray-400 text-sm">O stop deve estar onde a tese do trade é invalidada. Não em "quanto você aceita perder". Stop técnico, não emocional.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Deixe o Trade Desenvolver</h3>
                    <p className="text-gray-400 text-sm">Resista à tentação de interferir. O trade precisa de espaço para atingir o alvo. Microgerenciamento mata o RR.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">4</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Evite Sair por Emoção</h3>
                    <p className="text-gray-400 text-sm">Se o motivo para sair não existia no plano original, não saia. Emoção não é justificativa. Siga o plano.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância da Execução</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Você pode ter o melhor plano do mundo, mas se não executar, não adianta. A execução é onde a maioria dos traders falha.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Execução Consistente</h3>
              <p className="text-gray-300 mb-4">
                Consistência na execução significa fazer a mesma coisa, da mesma forma, trade após trade. Não importa se o último foi gain ou loss.
              </p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-400" />
                  <span>Entrada no preço planejado</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-400" />
                  <span>Stop onde definiu</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-400" />
                  <span>Alvo respeitado</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-400" />
                  <span>Sem interferência emocional</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Seguir o plano parece simples, mas é o mais difícil do trading. É onde a teoria encontra a realidade — e a realidade é brutal com quem não tem disciplina.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Scales size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Risk Reward Não Funciona Sozinho</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um erro comum é achar que basta ter um RR alto para ser lucrativo. Não é bem assim.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-amber-400 mb-4">A Relação com Win Rate</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 text-gray-400">Risk Reward</th>
                      <th className="text-left py-2 text-gray-400">Win Rate Mínimo para Empatar</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:1</td>
                      <td className="py-2">50%</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:2</td>
                      <td className="py-2">33%</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2">1:3</td>
                      <td className="py-2">25%</td>
                    </tr>
                    <tr>
                      <td className="py-2">1:4</td>
                      <td className="py-2">20%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed mb-4">
              Um RR de 1:4 parece ótimo, mas se seu win rate é 15%, você ainda perde dinheiro. <strong className="text-white">Contexto importa.</strong>
            </p>

            <p className="text-gray-300 leading-relaxed">
              O objetivo é encontrar o equilíbrio entre um RR favorável e um win rate sustentável para seu estilo de trading.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Crosshair size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Como Identificar Setups com Melhor RR</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Nem todo setup é igual. Alguns naturalmente oferecem um RR melhor. Aprender a identificá-los é um diferencial.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Entradas Mais Precisas</h3>
                <p className="text-gray-400 text-sm">Quanto mais precisa a entrada, menor o stop necessário. Stop menor = RR maior com o mesmo alvo.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Zonas de Liquidez</h3>
                <p className="text-gray-400 text-sm">Identifique onde o preço tem probabilidade de ir. Alvos em zonas de liquidez têm maior chance de serem atingidos.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Melhor Timing</h3>
                <p className="text-gray-400 text-sm">Entrar no momento certo reduz drawdown e aumenta a probabilidade de o trade desenvolver rapidamente.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              A busca por setups com melhor RR não é sobre ser ganancioso — é sobre ser estratégico. Escolher batalhas que você tem maior probabilidade de vencer.
            </p>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância dos Dados</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Você não pode melhorar o que não mede. Sem dados, você está operando no escuro.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">O que os dados revelam:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">RR médio real:</strong> Qual é seu RR efetivo ao longo de vários trades? Pode ser bem diferente do planejado.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Padrões de comportamento:</strong> Em quais situações você corta lucro cedo? Quando deixa prejuízo correr?</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Setups mais lucrativos:</strong> Quais setups naturalmente têm melhor RR para você?</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Analisar trades não é burocracia — é o caminho para melhorar. Os dados contam a verdade que suas emoções escondem.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <TrendUp size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Visão de Longo Prazo</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um único trade não define nada. O RR só faz sentido ao longo de muitos trades.
            </p>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Pense em séries, não em trades individuais:</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-red-400 font-semibold mb-2">Mentalidade de Curto Prazo</p>
                  <p className="text-gray-400 text-sm">"Esse trade precisa dar certo. Vou fechar com qualquer lucro para garantir."</p>
                </div>
                <div>
                  <p className="text-[#00FF85] font-semibold mb-2">Mentalidade de Longo Prazo</p>
                  <p className="text-gray-400 text-sm">"Esse é um de 100 trades. Vou executar o plano e deixar a matemática trabalhar."</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Consistência ao longo de vários trades é o que gera resultado. Não foque em um trade só — foque no processo que, repetido, gera lucro.
            </p>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Analise Seu Risk Reward de Verdade</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam a analisar risk reward ao longo de vários trades, facilitando a identificação de padrões e melhoria contínua. Descubra se seu RR real corresponde ao planejado.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Melhore Seu Risk Reward Hoje</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Melhorar seu risk reward depende de disciplina, execução e análise consistente. Não é sobre encontrar o setup perfeito — é sobre executar bem o que você já sabe.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Qual é um bom risk reward?</h3>
                <p className="text-gray-400 text-sm">Depende do seu win rate, mas em geral, buscar pelo menos 1:1.5 ou 1:2 é um bom começo. Quanto maior o RR, menor o win rate necessário para ser lucrativo.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">1:2 é suficiente?</h3>
                <p className="text-gray-400 text-sm">Com um RR de 1:2, você precisa acertar 33% dos trades para empatar. Se seu win rate é 40-50%, sim, é suficiente e lucrativo. Mas depende da sua execução real.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Dá pra lucrar com RR baixo?</h3>
                <p className="text-gray-400 text-sm">Sim, se o win rate compensar. Um RR de 1:1 com win rate de 60% é lucrativo. Mas é mais difícil manter win rate alto do que melhorar o RR. A matemática favorece RR maior.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como melhorar meu RR?</h3>
                <p className="text-gray-400 text-sm">Três caminhos: 1) Entradas mais precisas (stop menor); 2) Deixar trades atingirem o alvo (não fechar cedo); 3) Identificar setups com alvos naturalmente maiores. E acima de tudo: disciplina na execução.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-automatico"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades Automático</h3>
                <p className="text-gray-500 text-sm">Como automatizar o registro dos seus trades.</p>
              </Link>

              <Link
                to="/pt/por-que-traders-nao-evoluem"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Por Que Traders Não Evoluem</h3>
                <p className="text-gray-500 text-sm">Entenda os principais erros comportamentais no trading.</p>
              </Link>

              <Link
                to="/pt/por-que-consistencia-e-dificil-no-trading"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Consistência no Trading</h3>
                <p className="text-gray-500 text-sm">Por que consistência é difícil e como melhorar.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComoMelhorarRiskReward;
